/* ============================================================
   FRAGRANCE OASIS - cinematic interaction layer
   Progressive enhancement only. Every feature below degrades to
   a fully usable, shoppable page when JS is unavailable.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Header: transparent over the hero, black glass after scroll. */
  function initHeader() {
    var body = document.body;
    var threshold = 24;
    var ticking = false;

    // Only pages that actually carry the cinematic hero get a transparent
    // header at rest. Everywhere else the bar is solid from the start.
    body.classList.toggle('fo-has-hero', !!document.querySelector('.fo-hero'));

    function apply() {
      body.classList.toggle('fo-header-scrolled', window.scrollY > threshold);
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(apply);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  }

  /* Hero video: autoplay muted inline, paused offscreen and on hidden
     tabs. Poster carries the frame if playback is denied. */
  function initHeroVideo() {
    var videos = document.querySelectorAll('[data-fo-hero-video]');

    Array.prototype.forEach.call(videos, function (video) {
      if (video.dataset.foBound === '1') return;
      video.dataset.foBound = '1';

      var wrapper = video.closest('.fo-hero__media');
      var poster = wrapper ? wrapper.querySelector('.fo-hero__poster') : null;
      var visible = true;

      function showPosterOnly() {
        video.style.display = 'none';
        if (poster) poster.style.display = 'block';
      }

      if (reduceMotion.matches) {
        showPosterOnly();
        return;
      }

      var conn = navigator.connection;
      if (conn && conn.saveData === true) {
        showPosterOnly();
        return;
      }

      function tryPlay() {
        if (!visible || document.hidden) return;
        var p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(function () {
            showPosterOnly();
          });
        }
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              visible = entry.intersectionRatio > 0.25;
              if (visible) {
                tryPlay();
              } else if (!video.paused) {
                video.pause();
              }
            });
          },
          { threshold: [0, 0.25, 0.5] }
        );
        io.observe(video);
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          video.pause();
        } else {
          tryPlay();
        }
      });

      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.addEventListener('loadeddata', tryPlay, { once: true });
      }

      video.addEventListener('error', showPosterOnly);
    });
  }

  /* 3D product carousel */
  function initCarousel(root) {
    if (root.dataset.foBound === '1') return;
    root.dataset.foBound = '1';

    var stage = root.querySelector('[data-fo-stage]');
    if (!stage) return;

    var slides = Array.prototype.slice.call(stage.querySelectorAll('.fo-slide'));
    if (slides.length < 2) {
      root.classList.add('fo-carousel--ready');
      return;
    }

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-fo-dot]'));
    var live = root.querySelector('[data-fo-live]');
    var index = Math.floor(slides.length / 2);
    var autoplayDelay = parseInt(root.getAttribute('data-fo-autoplay') || '0', 10);
    var idleTimer = null;
    var paused = false;

    root.classList.add('fo-carousel--ready');

    function render() {
      slides.forEach(function (slide, i) {
        var offset = i - index;
        var half = Math.floor(slides.length / 2);
        // Shortest path around the ring so the loop never jumps.
        if (offset > half) offset -= slides.length;
        if (offset < -half) offset += slides.length;

        slide.dataset.pos = Math.abs(offset) <= 2 ? String(offset) : 'hidden';

        var isActive = offset === 0;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        // Only the active card is reachable by keyboard.
        Array.prototype.forEach.call(slide.querySelectorAll('a, button'), function (el) {
          if (isActive) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      });

      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      if (live) {
        var name = slides[index].getAttribute('data-fo-name') || '';
        live.textContent = name + ' - item ' + (index + 1) + ' of ' + slides.length;
      }
    }

    function go(next) {
      index = (next + slides.length) % slides.length;
      render();
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-fo-prev]'), function (b) {
      b.addEventListener('click', function () { go(index - 1); nudgeIdle(); });
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-fo-next]'), function (b) {
      b.addEventListener('click', function () { go(index + 1); nudgeIdle(); });
    });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { go(i); nudgeIdle(); });
    });

    // Clicking a receding card brings it forward.
    slides.forEach(function (slide, i) {
      slide.addEventListener('click', function (e) {
        if (slide.dataset.pos !== '0') {
          e.preventDefault();
          go(i);
          nudgeIdle();
        }
      });
    });

    stage.setAttribute('tabindex', '0');
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); nudgeIdle(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); nudgeIdle(); }
      if (e.key === 'Home') { e.preventDefault(); go(0); nudgeIdle(); }
      if (e.key === 'End') { e.preventDefault(); go(slides.length - 1); nudgeIdle(); }
    });

    var startX = null;
    var dragging = false;

    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      startX = e.clientX;
      dragging = true;
      stage.classList.add('is-dragging');
      paused = true;
    });

    stage.addEventListener('pointerup', function (e) {
      if (!dragging || startX === null) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 40) go(dx < 0 ? index + 1 : index - 1);
      dragging = false;
      startX = null;
      stage.classList.remove('is-dragging');
      nudgeIdle();
    });

    stage.addEventListener('pointercancel', function () {
      dragging = false;
      startX = null;
      stage.classList.remove('is-dragging');
    });

    // Autoplay only after inactivity, and never under reduced motion.
    function nudgeIdle() {
      paused = false;
      if (idleTimer) clearTimeout(idleTimer);
      if (!autoplayDelay || reduceMotion.matches) return;
      idleTimer = setTimeout(function tick() {
        if (!paused && !document.hidden) go(index + 1);
        idleTimer = setTimeout(tick, autoplayDelay);
      }, autoplayDelay);
    }

    ['mouseenter', 'focusin'].forEach(function (evt) {
      root.addEventListener(evt, function () { paused = true; });
    });
    ['mouseleave', 'focusout'].forEach(function (evt) {
      root.addEventListener(evt, function () { paused = false; });
    });

    render();
    nudgeIdle();
  }

  /* Quick add - uses Shopify's native AJAX cart endpoints. */
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-fo-add]');
      if (!btn) return;

      var id = btn.getAttribute('data-fo-add');
      if (!id) return;

      e.preventDefault();
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      fetch(routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          btn.textContent = 'Added';
          announce('Item added to your cart.');
          refreshCartCount();
          document.dispatchEvent(new CustomEvent('fo:cart:added'));
          setTimeout(function () {
            btn.textContent = original;
            btn.disabled = false;
          }, 1600);
        })
        .catch(function () {
          btn.textContent = 'Unavailable';
          setTimeout(function () {
            btn.textContent = original;
            btn.disabled = false;
          }, 1800);
        });
    });
  }

  function routeRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function announce(msg) {
    var region = document.getElementById('fo-live-region');
    if (!region) {
      region = document.createElement('div');
      region.id = 'fo-live-region';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    region.textContent = msg;
  }

  function refreshCartCount() {
    fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
        Array.prototype.forEach.call(bubbles, function (b) {
          b.textContent = cart.item_count;
          var parent = b.closest('.cart-count-bubble');
          if (parent) {
            parent.classList.remove('fo-cart-pulse');
            void parent.offsetWidth;
            parent.classList.add('fo-cart-pulse');
          }
        });
        // Dawn omits the bubble entirely at zero items - create it if needed.
        if (cart.item_count > 0 && bubbles.length === 0) {
          var icon = document.querySelector('#cart-icon-bubble');
          if (icon && !icon.querySelector('.cart-count-bubble')) {
            var el = document.createElement('div');
            el.className = 'cart-count-bubble';
            el.innerHTML = '<span aria-hidden="true">' + cart.item_count + '</span>';
            icon.appendChild(el);
          }
        }
      })
      .catch(function () { /* count refresh is cosmetic */ });
  }

  /* Sticky mobile add-to-cart on product pages. */
  function initStickyAtc() {
    var bar = document.querySelector('[data-fo-sticky-atc]');
    if (!bar) return;

    var anchor = document.querySelector('.product-form__buttons') ||
      document.querySelector('product-form form') ||
      document.querySelector('form[action*="/cart/add"]');
    if (!anchor) return;

    var trigger = bar.querySelector('[data-fo-sticky-submit]');
    if (trigger) {
      trigger.addEventListener('click', function () {
        var submit = anchor.querySelector('[type="submit"], .product-form__submit');
        if (submit) submit.click();
      });
    }

    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    io.observe(anchor);
  }

  /* Scroll reveals */
  function initReveals() {
    var items = document.querySelectorAll('.fo-inview:not(.is-in)');
    if (!items.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(items, function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    Array.prototype.forEach.call(items, function (el) { io.observe(el); });
  }

  function boot() {
    initHeader();
    initHeroVideo();
    initQuickAdd();
    initStickyAtc();
    initReveals();
    Array.prototype.forEach.call(document.querySelectorAll('[data-fo-carousel]'), initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Sections re-rendered by the theme editor need re-initialising.
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    Array.prototype.forEach.call(e.target.querySelectorAll('[data-fo-carousel]'), initCarousel);
    initHeroVideo();
    initReveals();
  });
})();
